import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import ROUTES from '@src/constants/route';
import sanitizeHtml from 'sanitize-html';
import { Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import queryString from 'query-string';
import camelcaseKeys from 'camelcase-keys';
import { PACKAGE_CHANGE_TYPES, PACKAGE_TYPE } from '@src/constants/package';
import { EVENT_TYPE, LANGUAGE } from '@src/constants';
import StackedAvatars from '@src/components/StackedAvatars';
import { capitalize } from 'lodash';
import { checkVietNam } from '@src/utils/checkCountry';
import { COLOR } from '@src/styles/color';
import {
  getFakeEndTimeDiscountForPackage,
  getPackageChangeType,
  getCustomPackagePrice,
  checkCustomPackage,
  checkStudioCustomPackage,
} from '@src/services/package';
import avatarMP11 from '@src/assets/avatars/avatar-mp-1-1.png';
import avatarMP12 from '@src/assets/avatars/avatar-mp-1-2.png';
import avatarMP13 from '@src/assets/avatars/avatar-mp-1-3.png';
import avatarMP21 from '@src/assets/avatars/avatar-mp-2-1.png';
import avatarMP22 from '@src/assets/avatars/avatar-mp-2-2.png';
import avatarMP23 from '@src/assets/avatars/avatar-mp-2-3.png';
import avatarMP31 from '@src/assets/avatars/avatar-mp-3-1.png';
import avatarMP32 from '@src/assets/avatars/avatar-mp-3-2.png';
import avatarMP33 from '@src/assets/avatars/avatar-mp-3-3.png';
import apis from '@src/apis';
import { getTotalPrice } from '@src/services/price';
import { CRM_URL } from '@src/configs';
import { PAYMENT_CURRENCY_UNIT } from '@src/constants/payment';

import {
  getOptimalVouchersForPackage,
  getPriceFromVoucher,
  getSavedPrice,
} from '@src/services/voucher';
import { DEFAULT_TIME_TO_RESET_COUNTDOWN } from '@src/constants/voucher';
import NewCountdownBanner from '../NewCountdownBanner';
import { StyledPackageItemWrapper } from '../index.style';
import PackageHeader from './components/PackageHeader';
import PackagePrice from './components/PackagePrice';
import CustomResources from '../CustomResources';
import PackageFeature from './components/PackageFeature';
import { StyledPackageCustomItem } from './index.style';

const CustomPackage = ({
  packages,
  pkg,
  isMobile,
  hasDiscountPackage,
  firstPaidVoucher,
  isRow,
  duration,
  currentUsingPackage,
  vouchers,
}) => {
  const { user = {} } = useSelector((state) => state.auth);

  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const history = useHistory();
  const location = useLocation();
  const { voucherCode, bannerType } = camelcaseKeys(
    queryString.parse(location.search) || {},
  );

  const { usingPackage } = useSelector((state) => state.user);
  const [optionsSelected, setOptionsSelected] = useState({
    credits: 0,
    devices: 0,
    voiceClonings: 0,
    concurrentRequestApi: 0,
  });
  const [
    remainingValueOfTheCurrentPackage,
    setRemainingValueOfTheCurrentPackage,
  ] = useState({ price: 0, usdPrice: 0 });
  const [adjustedPackagePrice, setAdjustedPackagePrice] = useState(0);
  const isCustomPackage = checkStudioCustomPackage(
    currentUsingPackage?.type,
    currentUsingPackage?.level,
  );

  const isPackageCustom = checkCustomPackage({
    level: pkg.level,
    type: pkg.type,
  });
  const isVietNam = checkVietNam();
  const { originalPrice, price, vndPrice } = getCustomPackagePrice({
    credits: optionsSelected.credits,
    voiceClonings: optionsSelected.voiceClonings,
    devices: optionsSelected.devices,
    concurrentRequestApi: optionsSelected.concurrentRequestApi,
    customResource: pkg?.customResource,
    isVietNam,
  });

  const [bestVoucher] = getOptimalVouchersForPackage({
    pkg: {
      ...pkg,
      price,
      vndPrice,
      usdPrice: price,
      custom: { credits: optionsSelected.credits },
    },
    vouchers,
  });

  const priceWithSaleOff = getPriceFromVoucher({
    isVietNam,
    voucher: bestVoucher,
    currPrice: price,
    currUsdPrice: price,
    roundedPrice: true,
  });

  const isDiscountUsingVoucher = bestVoucher?.value || originalPrice !== price;
  const isApplyFirstPaidVoucher =
    (bestVoucher?.description || 0) === (firstPaidVoucher?.description || 1);

  const getPreviousPackageInfo = () => {
    const apiUsingPackage = packages?.find(
      (pack) => pack?.code === user?.apiUsageOptions?.code,
    );
    const prevPackage = apiUsingPackage || usingPackage;
    const prevPackageExpiredDate = apiUsingPackage
      ? user?.apiPackage?.packageExpiryDate
      : user?.packageExpiryDate;
    return { prevPackage, prevPackageExpiredDate, apiUsingPackage };
  };

  const isUpgradePackage = useMemo(() => {
    const { prevPackage, prevPackageExpiredDate } = getPreviousPackageInfo();
    const determinePackageChange = () => {
      if (!usingPackage) return null;
      return getPackageChangeType({
        currPackage: pkg,
        prevPackage,
        prevPackageExpiredDate,
      });
    };

    return determinePackageChange() === PACKAGE_CHANGE_TYPES.UPGRADE;
  }, [pkg, usingPackage, getPreviousPackageInfo]);

  const sendEventAddToCartMoEngage = (packageCode, paymentUrl) => {
    const [product, packageItem, period] = pkg.code.split('-');
    const hasRemainingValue = isUpgradePackage || isCustomPackage;
    const hasSaleOff = hasDiscountPackage || isDiscountUsingVoucher;

    let finalPrice;
    if (hasRemainingValue) {
      finalPrice = adjustedPackagePrice;
    } else if (hasSaleOff) {
      finalPrice = priceWithSaleOff;
    } else {
      finalPrice = price;
    }

    const addToCartEventData = {
      event: EVENT_TYPE.ADD_TO_CART,
      userId: user?.id,
      email: user.email,
      package_code: packageCode,
      product,
      plan: packageItem,
      period,
      voucher_type: bestVoucher?.type,
      voucher_code: bestVoucher?.code,
      voucher_amount: bestVoucher?.value,
      voucher_title: isVietNam
        ? bestVoucher?.title?.vi
        : bestVoucher?.title?.en,
      package_price: pkg.price,
      price: finalPrice,
      ...(hasRemainingValue
        ? { remaining_package_value: remainingValueOfTheCurrentPackage?.price }
        : {}),
      isTopUp: false,
      min_credit_topup: undefined,
      min_price_topup: undefined,
      url: paymentUrl,
      currency_unit: isVietNam
        ? PAYMENT_CURRENCY_UNIT.VND
        : PAYMENT_CURRENCY_UNIT.USD,
      custom_credits: optionsSelected.credits,
      custom_devices: optionsSelected.devices,
      custom_max_cloning_voices: optionsSelected.voiceClonings,
      ...(optionsSelected.concurrentRequestApi && {
        custom_api_ccr: optionsSelected.concurrentRequestApi,
      }),
    };
    window.dataLayer.push(addToCartEventData);
  };

  const handleBuyPackage = async () => {
    const packageCode = pkg?.code;

    const { credits, devices, voiceClonings, concurrentRequestApi } =
      optionsSelected || {};
    const basePaymentParams = {
      package: packageCode,
      credits,
      devices,
      voiceClonings,
      ...(concurrentRequestApi !== undefined && { concurrentRequestApi }),
    };

    const hasVoucher = Boolean(voucherCode);
    const voucherPaymentParams = hasVoucher ? { voucherCode, bannerType } : {};

    const paymentParams = { ...basePaymentParams, ...voucherPaymentParams };
    const queryParams = queryString.stringify(paymentParams);

    // Push event to MoEngage
    const moEngagePaymentUrl = `${CRM_URL}${ROUTES.PAYMENT}${queryParams}`;
    sendEventAddToCartMoEngage(packageCode, moEngagePaymentUrl);

    history.push(`${ROUTES.PAYMENT}?${queryParams}`);
  };

  const onPriceChange = (key, customOptionValue) => {
    setOptionsSelected((prev) => ({ ...prev, [key]: customOptionValue }));
  };
  const onInitializePrices = (initialPrice) => {
    setOptionsSelected(initialPrice);
  };

  const MostPopularBanner = () => (
    <div className="most-popular-banner">
      <Typography className="most-popular-text">
        {capitalize(t('mostPopular'))}
      </Typography>
    </div>
  );
  const EmptyBanner = () => <div />;

  const renderBannerPackage = (discount = {}, mostPopular) => {
    const { startAt, endAt } = discount;
    const inDiscountTime = startAt && moment().isBetween(startAt, endAt);
    const endAtToShow = getFakeEndTimeDiscountForPackage({
      startTimeToCountdown: startAt,
      timeToResetCountdown: DEFAULT_TIME_TO_RESET_COUNTDOWN,
      endTimeToCountdown: endAt,
    });
    if (inDiscountTime) {
      return <NewCountdownBanner endAt={endAtToShow || {}} />;
    }
    if (mostPopular) return <MostPopularBanner />;
    return <EmptyBanner />;
  };

  const renderRandomAvatars = () => {
    const avatarsDay1List = [avatarMP11, avatarMP12, avatarMP13];
    const avatarsDay2List = [avatarMP21, avatarMP22, avatarMP23];
    const avatarsDay3List = [avatarMP31, avatarMP32, avatarMP33];
    const allAvatars = [avatarsDay1List, avatarsDay2List, avatarsDay3List];
    const currentDate = new Date();
    const dayOfWeek = currentDate.getDay();
    return allAvatars[dayOfWeek % allAvatars.length];
  };

  const getAndSetRemainingValueOfTheCurrentPackage = async (
    packageType,
    device,
    voiceClonings,
  ) => {
    const data = await apis.orders.remainingValueOfTheCurrentPackage({
      packageType,
      device,
      maxCloningVoices: voiceClonings,
    });

    if (data?.status) setRemainingValueOfTheCurrentPackage(data?.result);
  };

  useEffect(() => {
    // Get remaining value of the current package when using pricing model V3, not buy topup characters, upgrade package
    getAndSetRemainingValueOfTheCurrentPackage(
      location.pathname.includes('studio')
        ? PACKAGE_TYPE.STUDIO
        : PACKAGE_TYPE.API,
      optionsSelected.devices,
      optionsSelected.voiceClonings,
    );
  }, [location, optionsSelected]);

  useEffect(() => {
    if (price && pkg && remainingValueOfTheCurrentPackage) {
      const basePrice = price;
      const baseUsdPrice = price;

      const { price: remainingPrice = 0, usdPrice: remainingUsdPrice = 0 } =
        remainingValueOfTheCurrentPackage || {};

      const adjustedPrice = Math.max(0, basePrice - remainingPrice);
      const adjustedUsdPrice = Math.max(0, baseUsdPrice - remainingUsdPrice);

      const savedPrice = getSavedPrice({
        voucher: bestVoucher,
        currPrice: adjustedPrice,
        currUsdPrice: adjustedUsdPrice,
      });

      const totalRemainingPrice = getTotalPrice({
        voucher: bestVoucher,
        currPrice: adjustedPrice,
        currUsdPrice: adjustedUsdPrice,
        savedPrice,
      });

      setAdjustedPackagePrice(totalRemainingPrice);
    }
  }, [remainingValueOfTheCurrentPackage, bestVoucher, pkg, price, duration]);

  const ButtonBuyPackageView = () => (
    <Button
      fullWidth
      className={classNames('button-start', {
        'button-highlight': pkg.mostPopular,
      })}
      variant={pkg.mostPopular ? 'contained' : 'outlined'}
      data-id="add-to-cart"
      data-product={pkg?.type}
      data-package-code={pkg?.code}
      data-pricing-duration={duration}
      data-button-text={t('buyNow')}
      onClick={() => handleBuyPackage(null)}
    >
      {t('buyNow')}
    </Button>
  );

  const renderAvatarMostPopular = () => (
    <div className="popularity">
      <StackedAvatars images={renderRandomAvatars(false)} />
      <Typography
        className="popularity-text"
        variant="body2"
        sx={{ color: COLOR.black[64] }}
      >
        {t('mostPeopleChooseThis', { percent: pkg?.popularPercent || 86 })}
      </Typography>
    </div>
  );

  const renderAvatarOrPlaceholder = () => {
    if (pkg.mostPopular) {
      return renderAvatarMostPopular();
    }
    if (isMobile) {
      return null;
    }
    return <div style={{ height: '24px' }} />;
  };

  return (
    <StyledPackageItemWrapper
      isPoppular={pkg.mostPopular}
      isMobile={isMobile}
      isRow={isRow && isPackageCustom}
    >
      {renderBannerPackage(pkg.discountConfig, pkg.mostPopular)}
      <StyledPackageCustomItem
        isPoppular={pkg.mostPopular}
        isRow={isRow}
        isApiPack={pkg?.type === PACKAGE_TYPE.API}
        isMobile={isMobile}
        highlight={pkg.mostPopular}
        isDiscountPackage={
          hasDiscountPackage || (pkg.mostPopular && !hasDiscountPackage)
        }
        isVietNam={isVietNam}
      >
        <div className="card">
          {/* Package Header */}
          <div className="container-package-header">
            <PackageHeader
              pkg={pkg}
              bestVoucher={bestVoucher}
              isApplyFirstPaidVoucher={isApplyFirstPaidVoucher}
              language={language}
              isDiscountPackage={hasDiscountPackage}
            />
            <div className="package-feature-row">
              <PackageFeature isRow={isRow} isMobile={isMobile} pkg={pkg} />
            </div>
          </div>
          <div className="container-package-price-and-option">
            {/* Package Price */}
            <div className="container-package-price">
              <PackagePrice
                pkg={pkg}
                isVietNam={isVietNam}
                flags={{ isVietNam, isMobile }}
                discounts={{
                  shouldShowDiscount:
                    hasDiscountPackage || isDiscountUsingVoucher,
                }}
                pricing={{ originalPrice, price: priceWithSaleOff }}
              />

              {/* Card Content: Button, Resources & Features */}
              <div className="card-content">
                <ButtonBuyPackageView />
                {renderAvatarOrPlaceholder()}
                {!pkg?.mostPopular && <div className="box-range" />}
              </div>
            </div>
            <div className="package-feature-column">
              <PackageFeature
                isMobile={isMobile}
                pkg={pkg}
                optionsSelected={optionsSelected}
                bestVoucher={bestVoucher}
              />
            </div>
            {!!pkg?.featuresTitle && !isRow && (
              <Typography
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(
                    language === LANGUAGE.VN
                      ? pkg?.featuresTitle?.[language]
                      : pkg?.featuresTitle?.[LANGUAGE.EN],
                  ),
                }}
                className="title-text"
              />
            )}
            <div className="container-resource">
              <CustomResources
                pkg={pkg}
                onPriceChange={onPriceChange}
                onInitializePrices={onInitializePrices}
              />
            </div>
          </div>
        </div>
      </StyledPackageCustomItem>
    </StyledPackageItemWrapper>
  );
};

export default CustomPackage;
